import { useEffect, useState } from 'react';

import { NoticePopupLayout, NoticePopupListItems } from '@/pages/home/NoticePopupLayout';
import { articleOf, newestFirst } from '@/pages/home/noticePopupPosts';
import { FOREVER, isDismissed, rememberDismissal, todayKey } from '@/pages/home/noticePopupDismissals';
import { isNoticeListWindow, noticeIdOfThisWindow } from '@/pages/home/noticePopupOpener';
import { useNoticePopups } from '@/pages/home/useNoticePopups';

/**
 * 공지 팝업 <b>그 자체</b> — 새 창으로 열리는 화면 (사용자 지시 2026-09-10).
 *
 * <h2>사이드바 바깥이다</h2>
 * 라우터에서 {@code AppLayout} 밖에 세운다(로그인·퇴사설문과 같은 자리). 팝업 창 안에 사이드바와
 * 상단바가 함께 서면 그건 팝업이 아니라 <b>작게 연 홈</b>이다.
 *
 * <h2>버튼이 여기 있다</h2>
 * 「오늘 보지 않기」·「더이상 보지 않기」 는 아래 왼쪽, 「닫기」 는 아래 오른쪽. 누르면 기록을
 * 남기고({@code localStorage}) 창을 닫는다 — 홈과 <b>주소가 같아</b> 그 기록이 홈에도 보인다.
 *
 * <h2>어느 공지인지는 <b>주소가 아니라 창 이름</b>이 말한다</h2>
 * 팝업에도 주소 줄은 반드시 보이고(브라우저가 강제한다) 거기 id 를 실으면 36자리 UUID 가 공지
 * 위에 늘 떠 있게 된다. 그래서 주소는 {@code /notice-popup} 한 줄이고, id 는 창 이름으로 온다
 * ({@code noticeIdOfThisWindow}). 대신 <b>창 제목</b>에 공지 제목을 걸어, 작업표시줄에서도
 * 무슨 창인지 읽힌다.
 *
 * <h2>여럿이면 창 하나에 목록 (사용자 지시 2026-11-02)</h2>
 * 홈이 목록 창({@code isNoticeListWindow})으로 열었으면 「공지사항 N건」 목록을 세우고, 줄을 누르면
 * 같은 창 안에서 그 글을 펼친다. 「보지 않기」 는 그 글 하나의 것이라 창을 닫지 않고 목록으로
 * 돌아가며, 남은 글이 없을 때 닫는다.
 *
 * <h2>못 찾으면 조용히 닫는다</h2>
 * 기간이 끝났거나(서버 목록에서 빠진다) 볼 권한이 없는 글이면 세울 것이 없다. 「없는 공지입니다」
 * 를 띄워 봐야 창을 연 사람이 할 수 있는 일이 없다 — 열리자마자 닫히는 편이 낫다.
 */
export function NoticePopupWindow() {
  return isNoticeListWindow() ? <NoticeListWindow /> : <NoticeSingleWindow />;
}

function NoticeSingleWindow() {
  const id = noticeIdOfThisWindow();
  const popups = useNoticePopups();

  /*
    세울 것이 없는 경우 셋. 어느 쪽이든 <b>창을 닫는다</b> — 그리지 못한 채 열려 있는 흰 창은
    사람이 손쓸 데가 없다.

    ① 우리가 연 창이 아니다(창 이름에 표가 없다 — 주소를 직접 친 경우)
    ② 기간이 끝났거나 볼 권한이 없다(서버 목록에서 빠진다)
    ③ 목록을 못 받았다 — <b>로그아웃·세션 만료</b>가 여기다. 재시도를 다 쓴 뒤에야 참이 되므로
       잠깐 끊긴 것으로는 닫히지 않고, 닫혀도 다음에 홈에 들어오면 다시 뜬다
   */
  const post = (popups.data ?? []).find((item) => item.id === id) ?? null;
  const missing = id === null || (popups.data !== undefined && post === null) || popups.isError;

  useEffect(() => {
    if (missing) window.close();
  }, [missing]);

  // 창 제목은 주소 줄이 못 하는 일을 한다 — 창이 여럿 떠 있을 때 작업표시줄에서 고를 수 있게.
  useEffect(() => {
    if (post) document.title = `${post.title} — 공지사항`;
  }, [post]);

  if (!post) return null;

  const dismiss = (until: string) => {
    rememberDismissal(post.id, until);
    window.close();
  };

  return (
    <NoticePopupLayout
      title={post.title}
      date={new Date(post.createdAt).toLocaleDateString('ko-KR')}
      article={articleOf(post)}
      onDismissToday={() => dismiss(todayKey())}
      onDismissForever={() => dismiss(FOREVER)}
      onClose={() => window.close()}
    />
  );
}

function NoticeListWindow() {
  const popups = useNoticePopups();
  const [selected, setSelected] = useState<string | null>(null);
  const [read, setRead] = useState<string[]>([]);
  /** 이번에 「보지 않기」 를 누른 것 — 기억은 localStorage 에 있고, 이건 다시 그리게 하는 몫이다. */
  const [dismissedNow, setDismissedNow] = useState<string[]>([]);

  const items = newestFirst((popups.data ?? []).filter((post) => !isDismissed(post.id) && !dismissedNow.includes(post.id)));
  const missing = (popups.data !== undefined && items.length === 0) || popups.isError;

  useEffect(() => {
    if (missing) window.close();
  }, [missing]);

  // 남은 글이 하나면 목록 없이 그 글을 바로 펼친다.
  const current = items.find((post) => post.id === selected) ?? (items.length === 1 ? items[0] : null);

  useEffect(() => {
    document.title = current ? `${current.title} — 공지사항` : `공지사항 ${items.length}건`;
  }, [current, items.length]);

  if (items.length === 0) return null;

  if (current) {
    const dismiss = (until: string) => {
      rememberDismissal(current.id, until);
      setDismissedNow((prev) => [...prev, current.id]);
      setSelected(null);
    };

    return (
      <NoticePopupLayout
        title={current.title}
        date={new Date(current.createdAt).toLocaleDateString('ko-KR')}
        article={articleOf(current)}
        onBack={items.length > 1 ? () => setSelected(null) : undefined}
        onDismissToday={() => dismiss(todayKey())}
        onDismissForever={() => dismiss(FOREVER)}
        onClose={() => window.close()}
      />
    );
  }

  const dismissAllToday = () => {
    for (const post of items) rememberDismissal(post.id, todayKey());
    window.close();
  };

  return (
    <div className="notice-popup-window">
      <div className="notice-popup-window-head">
        <h1 className="font-semibold" style={{ fontSize: '13px', color: 'var(--text-primary)', minWidth: 0 }}>
          공지사항 {items.length}건
        </h1>
      </div>

      <div className="notice-popup-window-body">
        <NoticePopupListItems
          posts={items}
          read={read}
          onPick={(post) => {
            setSelected(post.id);
            setRead((prev) => (prev.includes(post.id) ? prev : [...prev, post.id]));
          }}
        />
      </div>

      <div className="notice-popup-window-actions">
        <button className="btn-ghost" style={{ fontSize: '12px' }} onClick={dismissAllToday}>
          모두 오늘 보지 않기
        </button>
        <button className="btn-primary ml-auto" style={{ fontSize: '13px', padding: '5px 16px' }} onClick={() => window.close()}>
          닫기
        </button>
      </div>
    </div>
  );
}
